import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaUpload } from 'react-icons/fa';
import axios from 'axios';
import { importZip, peekZipTarget } from '../../helpers/zipImporter';
import { apiFetch } from './apiFetch';

function IXIVARIANT() {
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [createFolderError, setCreateFolderError] = useState(null);
    const [deleteFolderError, setDeleteFolderError] = useState(null);
    const [renameFolderError, setRenameFolderError] = useState(null);
    const [isImporting, setIsImporting] = useState(false);

    const fetchFolders = async () => {
        try {
            const response = await apiFetch('/api/data/list');
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setFolders(data);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFolders();
    }, []);

    const handleCreateFolderClick = () => {
        setIsCreatingFolder(true);
        setNewFolderName('');
        setCreateFolderError(null);
    };

    const handleNewFolderNameChange = e => {
        // Normaliser en NFC dès la saisie pour éviter les écarts entre formes Unicode
        // composée/décomposée (ex: "é" saisi différemment selon le clavier/OS).
        const value = e.target.value.normalize('NFC');

            // Autoriser les lettres accentuées, les chiffres et les espaces.
            if (/^[\p{L}\p{N} ]*$/u.test(value)) {
            setNewFolderName(value);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return setCreateFolderError('Le nom ne peut être vide.');
        try {
            const res = await fetch('/api/data/create-folder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ folderName: newFolderName.trim().normalize('NFC') }),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || `Status ${res.status}`);
            }
            setIsCreatingFolder(false);
            fetchFolders();
        } catch (err) {
            console.error(err);
            setCreateFolderError(err.message);
        }
    };

    const handleCancelCreateFolder = () => {
        setIsCreatingFolder(false);
        setCreateFolderError(null);
    };

    const handleDeleteFolder = async folder => {
        if (!window.confirm(`Supprimer le référentiel "${folder}" ?`)) return;
        setDeleteFolderError(null);
        try {
            const res = await fetch(`/api/data/delete-folder/${folder}`, { method: 'DELETE', credentials: 'include' });
            if (!res.ok) {
                let errMsg = '';
                try {
                    const err = await res.json();
                    if (res.status === 404) {
                        errMsg = `Le dossier "${folder}" n'existe plus ou a déjà été supprimé.`;
                    } else {
                        errMsg = err.error || `Status ${res.status}`;
                    }
                } catch (e) {
                    errMsg = `Erreur inconnue (status ${res.status})`;
                }
                throw new Error(errMsg);
            }
            fetchFolders();
        } catch (err) {
            console.error(err);
            setDeleteFolderError(err.message);
        }
    };

    const handleRenameFolder = async folder => {
        setRenameFolderError(null);
        const renamed = window.prompt('Nouveau nom du référentiel :', folder);
        if (renamed === null) return;

        const nextName = renamed.trim().normalize('NFC');
        if (!nextName) {
            setRenameFolderError('Le nom ne peut être vide.');
            return;
        }

            if (!/^[\p{L}\p{N} ]+$/u.test(nextName)) {
                setRenameFolderError('Nom invalide : utilisez uniquement des lettres, chiffres et espaces.');
            return;
        }

        if (nextName === folder) {
            return;
        }

        try {
            const res = await apiFetch('/api/data/rename-folder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ oldFolderName: folder, newFolderName: nextName }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Status ${res.status}`);
            }

            fetchFolders();
        } catch (err) {
            console.error(err);
            setRenameFolderError(err.message);
        }
    };

    const downloadZip = async (url, fileName) => {
        try {
            const response = await axios.get(url, {
                responseType: "blob",
                withCredentials: true
            });

            const objectUrl = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = objectUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error(err);
            alert("Erreur lors du téléchargement");
        }
    };

    // Ne retirer que les caractères réellement interdits dans un nom de fichier Windows,
    // pour garder les lettres accentuées (ex: "Référentiel" reste "Référentiel", pas "R_f_rentiel").
    const safeFileName = (name) => name.replace(/[\\/:*?"<>|\x00-\x1F]/g, "_").trim();

    const handleDownloadFolder = (folder) => {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const email = authUser?.user_EMAIL;
        if (!email) {
            alert("Utilisateur introuvable, veuillez vous reconnecter.");
            return;
        }

        downloadZip(
            `/api/content/downloadProject/${encodeURIComponent(email)}/${encodeURIComponent(folder)}`,
            `${safeFileName(folder)}.zip`
        );
    };

    const handleImportZip = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.zip';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const peek = await peekZipTarget(file);
                let chapterNameOverride;

                if (peek.type === 'referential') {
                    // Le nom lu dans le zip peut venir d'un autre OS (ex: macOS normalise
                    // les noms de fichiers en NFD) : on le ramène en NFC pour matcher les
                    // noms de dossiers déjà présents sur le serveur.
                    let candidateName = peek.chapterName.normalize('NFC');
                    while (folders.includes(candidateName)) {
                        const newName = window.prompt(
                            `Un référentiel nommé "${candidateName}" existe déjà. Choisissez un autre nom pour l'import (Annuler pour arrêter) :`,
                            candidateName
                        );
                        if (newName === null) return;

                        const trimmed = newName.trim().normalize('NFC');
                        if (!trimmed) continue;
                            if (!/^[\p{L}\p{N} ]+$/u.test(trimmed)) {
                                alert('Nom invalide : utilisez uniquement des lettres, chiffres et espaces.');
                            continue;
                        }
                        candidateName = trimmed;
                    }
                    chapterNameOverride = candidateName;
                }

                setIsImporting(true);
                const res = await importZip(file, { chapterNameOverride });

                if (res.type === 'referential') {
                    alert(`Import du référentiel terminé !\nRéférentiel : "${res.targetChapterName}"\n${res.variantCount} variante(s) importée(s)\n✓ ${res.successCount} fichier(s) importé(s)\n✗ ${res.errorCount} échec(s)`);
                } else {
                    alert(`Import terminé !\nNom d'origine détecté : "${res.originalVariantName}"\nRenommé en : "${res.targetFolderName}"\n✓ ${res.successCount} fichier(s) importé(s) dans ${res.targetChapterName}/${res.targetFolderName}\n✗ ${res.errorCount} échec(s)`);
                }

                await fetchFolders();
            } catch (err) {
                console.error('Erreur lors de l\'import :', err);
                alert('Erreur lors de l\'import : ' + err.message);
            } finally {
                setIsImporting(false);
            }
        };

        input.click();
    };

    if (loading) return <div>Chargement...</div>;
    if (error) return <div className="alert alert-danger">Erreur : {error.message}</div>;

    return (
        <div className="page-content">
            <div className="container mt-8">
                <div className="card shadow-sm p-4 position-relative">
                    {isImporting && (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                zIndex: 10,
                                borderRadius: 'inherit'
                            }}
                        >
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <div className="text-primary fw-bold">Import en cours, veuillez patienter...</div>
                        </div>
                    )}
                    <h1 className='text-primary text-center'>iXiMAKER</h1>
                    <h2 className="text-tertiary mb-4 text-center">Liste des Référentiels</h2>
                    <p className="text-secondary mb-4 text-center" title="Chaque référentiel contient les différentes variantes d'une procédure."> ℹ️Sélectionnez un référentiel, ou{' '}
                        <button
                            className="text-primary p-0 m-0 bg-transparent border-0 cursor-pointer"
                            onClick={handleCreateFolderClick}
                        > créez-en un nouveau.
                        </button>
                    </p>
                    {folders.length > 0 ? (
                        <div className="row">
                            {[0, 1].map(colIdx => (
                                <div className="col-md-6" key={colIdx}>
                                    <ul className="list-group">
                                        {folders
                                            .filter((_, idx) => idx % 2 === colIdx)
                                            .map(folder => (
                                                <li key={folder} className="list-group-item d-flex justify-content-between align-items-center font-size-20">
                                                    <Link to={`/languageManageWork/${folder}?from=variant`} className="text-decoration-none text-primary">
                                                        <i className='mdi mdi-folder-multiple-outline'></i> {folder}
                                                    </Link>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            onClick={() => handleRenameFolder(folder)}
                                                            className="btn btn-sm btn-outline-secondary"
                                                            title={`Renommer le référentiel "${folder}"`}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            title="Télécharger ce référentiel (zip)"
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => handleDownloadFolder(folder)}
                                                        >
                                                            <FaUpload />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteFolder(folder)}
                                                            className="btn btn-sm btn-outline-danger"
                                                            title={`Supprimer le référentiel "${folder}"`}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Aucun référentiel trouvé.</p>
                    )}
                    <div className="mb-3 mt-4 d-flex justify-content-center">
                        {!isCreatingFolder ? (
                            <div className="d-flex gap-2">
                                <button onClick={handleCreateFolderClick} className="btn btn-outline-primary"
                                    disabled={isImporting}
                                    title="Créer un nouveau référentiel vierge">
                                    <i className="mdi mdi-folder-multiple-plus-outline fs-3 align-middle"></i> Créer un référentiel
                                </button>
                                <button onClick={handleImportZip} className="btn btn-outline-success ms-2"
                                    disabled={isImporting}
                                    title="Importer un référentiel (ou une variante) depuis un fichier ZIP">
                                    <i className="mdi mdi-upload fs-3 align-middle"></i> Importer depuis ZIP
                                </button>
                            </div>
                        ) : (
                            <div className='col-4 d-flex flex-column align-items-center'>
                                <h3 className="text-primary mb-3">Créer un référentiel</h3>
                                <input
                                    type="text"
                                    className="form-control mb-2"
                                    placeholder="Référence ou nom de l'objet"
                                    value={newFolderName}
                                    onChange={handleNewFolderNameChange}
                                    id="new-variant-folder-input"
                                />
                                {createFolderError && <div className="alert alert-danger">{createFolderError}</div>}
                                <div className="d-flex justify-content-between">
                                    <button onClick={handleCreateFolder} className="btn btn-primary me-2">Créer</button>
                                    <button onClick={handleCancelCreateFolder} className="btn btn-outline-secondary">Annuler</button>
                                </div>
                            </div>
                        )}
                        {deleteFolderError && <div className="alert alert-danger mt-2">{deleteFolderError}</div>}
                        {renameFolderError && <div className="alert alert-danger mt-2">{renameFolderError}</div>}
                    </div>
                    <p className="text-secondary mb-4 text-center"
                        title="Chaque référentiel contient les différentes variantes d'une procédure.">
                            ℹ️ Les noms de référentiels peuvent contenir des lettres accentuées, des chiffres et des espaces. Les caractères spéciaux sont interdits. </p>
                    <p className="text-secondary mb-0 text-center">
                        ⚠️ La suppression d'un référentiel est irréversible et supprimera toutes les variantes associées. Assurez-vous de sauvegarder toute information importante avant de procéder.
                    </p>

                </div>
            </div>
        </div>
    );
}

export default IXIVARIANT;
